/* eslint-disable parog-web/no-glint-expect-error-directive */
import { fn } from '@ember/helper';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import Anxiety from 'parog-web/components/feeling-evas/sliders/anxiety';
import Energy from 'parog-web/components/feeling-evas/sliders/energy';
import Irritability from 'parog-web/components/feeling-evas/sliders/irritability';
import Mood from 'parog-web/components/feeling-evas/sliders/mood';
import SleepDuration from 'parog-web/components/feeling-evas/sliders/sleep-duration';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import FeelingEvasDeclaredJournalEvent from 'parog-web/models/feeling-evas-declared-journal-event';
import type SurveyRequestedJournalEvent from 'parog-web/models/survey-requested-journal-event';

interface Args extends ForcBaseArgs {
  surveyRequested: SurveyRequestedJournalEvent;
}

export default class FeelingEvasFormComponent extends JournalEventBasedComponent<Args> {
  static override forcDisplayName = 'Ressenti patient';
  static override fullComponentId = 'feeling-evas/form';
  static override journalEventClass = FeelingEvasDeclaredJournalEvent;

  override journalEventProperties = [
    'occurredAt',
    'mood',
    'energy',
    'irritability',
    'sleepDuration',
    'anxiety',
    'comments'
  ];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  sliderStyles = 'mt-[1.5em]';

  override get defaultModel() {
    return {
      mood: 0,
      energy: 0,
      irritability: 0,
      sleepDuration: 0,
      anxiety: 0
    };
  }

  labels = (labels: Record<string, unknown>, value: string) => {
    if (!labels[value]) return '';

    // HACK: using "&nbsp;" here to fix a positionning bug
    return '&nbsp;' + labels[value];
  };

  override async save() {
    this.changeset.set('occurredAt', new Date());
    const journalEvent = await super.save();

    if (this.args.surveyRequested) {
      const surveyCompleted = this.store.createRecord(
        'survey-completed-journal-event',
        {
          patient: this.patient,
          occurredAt: new Date(),
          rawInputs: this.model,
          linkedJournalEventIds: [journalEvent.id],
          formId: 'feeling-evas/form',
          surveyRequestedJournalEvent: this.args.surveyRequested
        }
      );

      await surveyCompleted.save();
      this.analytics.trackPatientMeasurement('eva');
    }
  }

  setProperty = (propertyName: string, value: number) => {
    const precision = 10;
    const roundedValue = Math.round(value * precision) / precision;

    if (roundedValue != value) {
      console.warn(
        'FUI slider.js rounding bug happened. We had to re-round the value. See https://github.com/fomantic/Fomantic-UI/issues/1545'
      );
    }

    if (this.changeset.get(propertyName) === roundedValue) return;

    this.changeset.set(propertyName, roundedValue);
  };

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <h3>Au cours des 7 derniers jours</h3>
        <div class='field' data-test-field='mood'>
          <label>Quelle a été mon humeur ?</label>
          <Mood
            class={{this.sliderStyles}}
            @start={{changesetGet this.changeset 'mood'}}
            {{! @glint-expect-error }}
            @onChange={{fn this.setProperty 'mood'}}
          />
        </div>

        <div class='ui divider'></div>

        <div class='field' data-test-field='energy'>
          <label>Quel a été mon niveau d'énergie physique ?</label>
          <Energy
            class={{this.sliderStyles}}
            @start={{changesetGet this.changeset 'energy'}}
            {{! @glint-expect-error }}
            @onChange={{fn this.setProperty 'energy'}}
          />
        </div>

        <div class='ui divider'></div>

        <div class='field' data-test-field='irritability'>
          <label>Quel a été mon niveau d'irritabilité ?</label>
          <Irritability
            class={{this.sliderStyles}}
            @start={{changesetGet this.changeset 'irritability'}}
            {{! @glint-expect-error }}
            @onChange={{fn this.setProperty 'irritability'}}
          />
        </div>

        <div class='ui divider'></div>

        <div class='field' data-test-field='sleep-duration'>
          <label>Quelle a été la durée de mon sommeil ?</label>
          <SleepDuration
            class={{this.sliderStyles}}
            @start={{changesetGet this.changeset 'sleepDuration'}}
            {{! @glint-expect-error }}
            @onChange={{fn this.setProperty 'sleepDuration'}}
          />
        </div>

        <div class='ui divider'></div>

        <div class='field' data-test-field='anxiety'>
          <label>Quel a été mon niveau d'anxiété ou d'angoisse ?</label>
          <Anxiety
            class={{this.sliderStyles}}
            @start={{changesetGet this.changeset 'anxiety'}}
            {{! @glint-expect-error }}
            @onChange={{fn this.setProperty 'anxiety'}}
          />
        </div>
        <div class='field'>
          <Form.TextArea @labelName='Commentaires' @propertyName='comments' />
        </div>
      </:content>
    </Provider>
  </template>
}
